import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input as ReactstrapInput } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import {
  PageWrapper,
  HeaderSection,
  TableContainer,
  Header,
  Tabs,
  TabButton,
  Section,
  FormRow,
  FormRowGroup,
  Label as StyledLabel,
  Input,
  TextArea,
  Select,
  FileInput,
  SaveButton,
  CancelButton,
  StyledTable,
  CreateButton,
  FileListItem,
  FileRemoveButton,
  FileDownloadButton,
  ChooseFileButton,
  FileListContainer,
  VocabCount,
  FilterTag,
  FileNameSpan,
  VocabCopyButton,
  FileItemBox,
  VocabTestBox,
  WarningButton,
  DangerButton,
  SecondaryButton,
  SuccessButton,
  PrimaryButton,
} from './LessonDiary.styled';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { lessonDiaryActions, classActions, scheduleActions, userActions, studentActions, attendanceActions, attendanceStudentRecordActions } from '../../redux/actions';
import { reviewSessionActions } from '../../redux/actions/reviewSession.actions';
import { downloadVocabTest } from '../../redux/actions/vocabTest.actions';
import { submitText } from '../../redux/actions';
import Loading from '../../common/Loading/loading';

interface Schedule {
  id: number;
  classId: number;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

interface MonthlySession {
  scheduleId: number;
  lessonDate: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  displayText: string;
}

const LessonDiary: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDiary, setEditingDiary] = useState<any>(null);
  const [vocabText, setVocabText] = useState('');
  const [sampleSentences, setSampleSentences] = useState('');
  const [homework, setHomework] = useState('');
  const [invalidLines, setInvalidLines] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<string[]>([]);
  const [removedFiles, setRemovedFiles] = useState<string[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true); // Loading cho việc tải dữ liệu ban đầu
  const [selectedClassFilter, setSelectedClassFilter] = useState<number | null>(null);
  const [selectedTeacherFilter, setSelectedTeacherFilter] = useState<number | null>(null); // null = Tất cả
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // State cho vocab test modal
  const [showVocabTestModal, setShowVocabTestModal] = useState(false);
  const [selectedDiaryForTest, setSelectedDiaryForTest] = useState<any>(null);
  const [numberOfStudents, setNumberOfStudents] = useState<number>(1);

  // State cho flashcard modal
  const [showFlashcardModal, setShowFlashcardModal] = useState(false);
  const [selectedDiaryForFlashcard, setSelectedDiaryForFlashcard] = useState<any>(null);
  const [numberOfStudentsFlashcard, setNumberOfStudentsFlashcard] = useState<number>(1);
  const [flashcardVocabText, setFlashcardVocabText] = useState<string>('');

  // State cho Writing modal
  const [showWritingModal, setShowWritingModal] = useState(false);
  const [selectedDiaryForWriting, setSelectedDiaryForWriting] = useState<any>(null);
  const [numberOfStudentsWriting, setNumberOfStudentsWriting] = useState<number>(0);

  // State cho modal xem từ vựng/mẫu câu
  const [showVocabViewModal, setShowVocabViewModal] = useState(false);
  const [showSampleSentencesViewModal, setShowSampleSentencesViewModal] = useState(false);
  const [viewModalContent, setViewModalContent] = useState('');
  const [viewModalTitle, setViewModalTitle] = useState('');

  // State cho loading khi tạo flashcard set
  const [creatingFlashcardSetId, setCreatingFlashcardSetId] = useState<number | null>(null);

  // State cho review session
  const [creatingReviewId, setCreatingReviewId] = useState<number | null>(null);
  const [copiedReviewId, setCopiedReviewId] = useState<number | null>(null);
  // Modal cảnh báo định dạng mẫu câu
  const [showSentenceFormatModal, setShowSentenceFormatModal] = useState(false);

  // ── State cho modal tải nhật kí ──────────────────────────────────────────
  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const toDateStr = (d: Date) => d.toISOString().split('T')[0];

  const [showDownloadDiaryModal, setShowDownloadDiaryModal] = useState(false);
  const [dlClassId, setDlClassId] = useState<number | null>(null);
  const [dlDateFrom, setDlDateFrom] = useState(toDateStr(oneMonthAgo));
  const [dlDateTo, setDlDateTo] = useState(toDateStr(today));
  const [dlSections, setDlSections] = useState({
    vocabulary: true,
    sampleSentences: true,
    contentTaught: true,
    homework: true,
  });
  const [dlFormat, setDlFormat] = useState<'doc' | 'pdf'>('doc');
  const [dlLoading, setDlLoading] = useState(false);

  // Mở modal download, auto-select lớp đang filter
  const handleOpenDownloadModal = () => {
    const _today = new Date();
    const _oneMonthAgo = new Date(_today);
    _oneMonthAgo.setMonth(_oneMonthAgo.getMonth() - 1);
    setDlClassId(selectedClassFilter);
    setDlDateFrom(toDateStr(_oneMonthAgo));
    setDlDateTo(toDateStr(_today));
    setDlSections({ vocabulary: true, sampleSentences: true, contentTaught: true, homework: true });
    setDlFormat('doc');
    setShowDownloadDiaryModal(true);
  };

  // Hàm fetch nhật kí theo khoảng ngày — dùng fetchWithAuth (accessToken) như exam service
  const fetchDiariesForRange = async (classId: number, from: string, to: string) => {
    const { fetchWithAuth: fetchAuth } = await import('../../helpers');
    const res = await fetchAuth(
      `${process.env.REACT_APP_API_URL}/api/lesson_diaries/by-range?classId=${classId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `HTTP ${res.status}`);
    }
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  };

  // Tạo HTML và trigger download (doc / pdf)
  const handleDownloadDiary = async () => {
    if (!dlClassId) { alert('Vui lòng chọn lớp!'); return; }
    if (!dlDateFrom || !dlDateTo) { alert('Vui lòng chọn khoảng thời gian!'); return; }
    if (dlDateFrom > dlDateTo) { alert('Ngày bắt đầu phải trước ngày kết thúc!'); return; }
    if (!Object.values(dlSections).some(Boolean)) {
      alert('Vui lòng chọn ít nhất 1 mục nội dung để xuất!'); return;
    }
    setDlLoading(true);
    try {
      const diaries = await fetchDiariesForRange(dlClassId, dlDateFrom, dlDateTo);
      if (diaries.length === 0) {
        alert('Không tìm thấy nhật kí nào trong khoảng thời gian này!');
        setDlLoading(false);
        return;
      }
      const className = getClassName(dlClassId);
      const fmtDate = (s: string) => {
        if (!s) return '';
        const d = s.split('T')[0].split('-');
        return `${d[2]}/${d[1]}/${d[0]}`;
      };
      const sectionLabel = [
        dlSections.vocabulary && 'Từ vựng',
        dlSections.sampleSentences && 'Mẫu câu',
        dlSections.contentTaught && 'Nội dung đã dạy',
        dlSections.homework && 'BTVN',
      ].filter(Boolean).join(' · ');

      const escHtml = (s: string) => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

      const buildSection = (label: string, content: string, labelColor: string) => {
        if (!content || !content.trim()) return '';
        const lines = content.split('\n').filter((l: string) => l.trim());
        const inner = lines.map((l: string) => `<p style="margin:2px 0;font-size:13px;">${escHtml(l)}</p>`).join('');
        return `<div style="margin-bottom:8px;">
          <div style="font-size:10px;font-weight:bold;color:${labelColor};text-transform:uppercase;letter-spacing:.5px;margin-bottom:2px;">${label}</div>
          ${inner}
        </div>`;
      };

      const cellBorder = '1px solid #d1d5db';
      const rows = diaries.map((d: any, idx: number) => {
        const dateLabel = fmtDate(d.lessonDate);
        // Ưu tiên dùng snapshot lưu trong nhật kí, fallback sang schedulesList cho record cũ
        const hasSnapshot = d.sessionDayOfWeek != null && d.sessionStartTime;
        const scheduleForLabel = hasSnapshot
          ? { dayOfWeek: d.sessionDayOfWeek, startTime: d.sessionStartTime, endTime: d.sessionEndTime }
          : schedulesList.find((s: any) => s.id === d.scheduleId);
        const scheduleLabel = scheduleForLabel
          ? `${convertDayOfWeekToVietnamese(scheduleForLabel.dayOfWeek)} (${convertTimeToVietnamese(scheduleForLabel.startTime, scheduleForLabel.endTime)})`
          : '';
        const sections = [
          dlSections.vocabulary && buildSection('Từ vựng', d.vocabulary || '', '#4f46e5'),
          dlSections.sampleSentences && buildSection('Mẫu câu', d.sampleSentences || '', '#0284c7'),
          dlSections.contentTaught && buildSection('Nội dung đã dạy', d.contentTaught || '', '#16a34a'),
          dlSections.homework && buildSection('BTVN', d.homework || '', '#dc2626'),
        ].filter(Boolean).join('');
        return `<tr style="background:${idx % 2 === 0 ? '#f9fafb' : '#ffffff'};">
          <td style="border:${cellBorder};padding:10px 12px;text-align:center;font-size:13px;font-weight:600;color:#374151;white-space:nowrap;vertical-align:top;">
            ${dateLabel}<br/><span style="font-size:11px;color:#9ca3af;font-weight:normal;">${escHtml(scheduleLabel)}</span>
          </td>
          <td style="border:${cellBorder};padding:10px 14px;vertical-align:top;">
            ${sections || '<span style="color:#9ca3af;">—</span>'}
          </td>
        </tr>`;
      }).join('');

      // HTML body (shared between doc & pdf)
      const bodyHtml = `
        <div style="text-align:center;margin-bottom:20px;">
          <div style="font-size:10px;letter-spacing:2px;text-transform:uppercase;color:#6366f1;font-weight:bold;">Nhật kí dạy học</div>
          <div style="font-size:22px;font-weight:900;color:#111827;margin:4px 0;">${escHtml(className)}</div>
          <div style="font-size:12px;color:#6b7280;">Từ ${fmtDate(dlDateFrom)} đến ${fmtDate(dlDateTo)} &nbsp;&middot;&nbsp; ${sectionLabel}</div>
          <div style="width:50px;height:3px;background:#6366f1;border-radius:2px;margin:8px auto 0;"></div>
        </div>
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr>
            <th style="border:${cellBorder};padding:10px 12px;background:#4f46e5;color:#fff;font-weight:bold;text-align:center;width:110px;">Buổi học</th>
            <th style="border:${cellBorder};padding:10px 12px;background:#4f46e5;color:#fff;font-weight:bold;text-align:left;">Nội dung</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div style="text-align:right;margin-top:12px;font-size:10px;color:#9ca3af;">Xuất ngày ${fmtDate(toDateStr(new Date()))}</div>`;

      const baseName = `NhatKi_${className.replace(/\s+/g,'_')}_${dlDateFrom}_den_${dlDateTo}`;

      if (dlFormat === 'doc') {
        // Word-compatible HTML (MS Word supports mso namespace)
        const docHtml = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
  xmlns:w="urn:schemas-microsoft-com:office:word"
  xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="UTF-8"/>
  <meta name="ProgId" content="Word.Document"/>
  <meta name="Generator" content="Microsoft Word 15"/>
  <meta name="Originator" content="Microsoft Word 15"/>
  <style>
    * { box-sizing: border-box; }
    body { font-family: 'Times New Roman', serif; font-size: 13pt; color: #111827; margin: 1.5cm; }
    table { border-collapse: collapse; width: 100%; }
    td, th { border: 1px solid #d1d5db; padding: 8pt; vertical-align: top; }
    @page { margin: 1.5cm; size: A4; }
  </style>
</head>
<body>${bodyHtml}</body>
</html>`;
        const blob = new Blob([docHtml], { type: 'application/msword' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${baseName}.doc`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // PDF: gọi BE endpoint — dùng PDFKit + arial-unicode-ms.ttf (hỗ trợ tiếng Việt)
        const diariesForPdf = diaries.map((d: any) => {
          const hasSnap = d.sessionDayOfWeek != null && d.sessionStartTime;
          const schedForPdf = hasSnap
            ? { dayOfWeek: d.sessionDayOfWeek, startTime: d.sessionStartTime, endTime: d.sessionEndTime }
            : schedulesList.find((s: any) => s.id === d.scheduleId);
          return {
            lessonDate: d.lessonDate,
            scheduleLabel: schedForPdf
              ? `${convertDayOfWeekToVietnamese(schedForPdf.dayOfWeek)} (${convertTimeToVietnamese(schedForPdf.startTime, schedForPdf.endTime)})`
              : '',
            vocabulary:      d.vocabulary     || '',
            sampleSentences: d.sampleSentences || '',
            contentTaught:   d.contentTaught  || '',
            homework:        d.homework       || '',
          };
        });

        const { fetchWithAuth: fetchAuth } = await import('../../helpers');
        const pdfRes = await fetchAuth(
          `${process.env.REACT_APP_API_URL}/api/lesson_diaries/export-pdf`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              className,
              dateFrom: dlDateFrom,
              dateTo:   dlDateTo,
              sections: dlSections,
              diaries:  diariesForPdf,
            }),
          }
        );
        if (!pdfRes.ok) {
          const e = await pdfRes.json().catch(() => ({}));
          throw new Error((e as any).message || 'Lỗi tạo PDF');
        }
        const blob = await pdfRes.blob();
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${baseName}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      setShowDownloadDiaryModal(false);
    } catch (err) {
      console.error(err);
      alert('Có lỗi khi tải nhật kí!');
    } finally {
      setDlLoading(false);
    }
  };

  // State cho modal xem từ vựng
  const [viewModalDiary, setViewModalDiary] = useState<any>(null);

  // Form data
  const [formData, setFormData] = useState({
    classId: '',
    scheduleId: '',
    lessonDate: '',
    contentPlanned: '',
    contentTaught: '',
    note: '',
  });

  // State cho danh sách các buổi học trong tháng của lớp được chọn
  const [monthlySessions, setMonthlySessions] = useState<MonthlySession[]>([]);

  // State cho tháng/năm của modal "Ca học" (khác với filter tháng/năm ở trên)
  const [modalMonth, setModalMonth] = useState<number>(new Date().getMonth() + 1);
  const [modalYear, setModalYear] = useState<number>(new Date().getFullYear());

  // State để lưu trạng thái ban đầu của form (để phát hiện thay đổi)
  const [initialFormState, setInitialFormState] = useState<{
    formData: typeof formData;
    vocabText: string;
    sampleSentences: string;
    homework: string;
    uploadedFiles: File[];
    existingFiles: string[];
  } | null>(null);

  const dispatch = useAppDispatch();
  const classes = useAppSelector((state: any) => state.classes.classesList || []);
  const schedulesList = useAppSelector((state: any) => state.schedules.schedulesList || []);
  const lessonDiariesList = useAppSelector((state: any) => state.lessonDiaries?.lessonDiariesList || []);
  const teachers = useAppSelector((state: any) => state.users.items || []);
  const students = useAppSelector((state: any) => state.students.studentsList || []);
  const attendanceSessions = useAppSelector((state: any) => state.attendanceSessions.attendanceSessionsList || []);
  const attendanceStudentRecords = useAppSelector((state: any) => state.attendanceStudentRecords.attendanceStudentRecordsList || []);

  // Lấy thông tin user hiện tại
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const isAdmin = userInfo.role === 0;

  // Tạo danh sách các năm (năm hiện tại ± 2)
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  useEffect(() => {
    setIsLoadingData(true);
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    Promise.all([
      dispatch(classActions.getAll()),
      dispatch(scheduleActions.getAll()),
      dispatch(lessonDiaryActions.getByMonth(currentYear, currentMonth)),
      dispatch(userActions.getAllTeachers()),
      dispatch(studentActions.getAll()),
      dispatch(attendanceActions.getAllAttendanceSessions())
      // BỎ getAllAttendanceStudentRecords - chỉ load khi cần thiết
    ])
      .then(() => {
        setIsLoadingData(false);
      })
      .catch((err) => {
        console.error('Error loading data:', err);
        setIsLoadingData(false);
      });
  }, [dispatch]);

  // useEffect để load lại nhật ký khi thay đổi tháng/năm
  useEffect(() => {
    if (selectedMonth && selectedYear) {
      setIsLoadingData(true);

      (async () => {
        try {
          await dispatch(lessonDiaryActions.getByMonth(selectedYear, selectedMonth));
          setIsLoadingData(false);
        } catch (err) {
          console.error('Error loading lesson diaries:', err);
          setIsLoadingData(false);
        }
      })();
    }
  }, [selectedMonth, selectedYear, dispatch]);

  useEffect(() => {
    const lines = vocabText.split('\n');
    const linesWithoutColon = lines.filter(line => line.trim() !== '' && !line.includes(':'));
    setInvalidLines(linesWithoutColon);
  }, [vocabText]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.scrollTop = 0;
    }
  }, [vocabText]);

  // Hàm mở modal vocab test
  const handleOpenVocabTestModal = (diary: any) => {
    setSelectedDiaryForTest(diary);
    // Đếm số học sinh của lớp
    const classStudents = students.filter((s: any) => s.classId === diary.classId && s.startDate !== null);
    setNumberOfStudents(classStudents.length || 1);
    setShowVocabTestModal(true);
  };

  // Hàm download vocab test
  const handleDownloadVocabTest = async () => {
    if (!selectedDiaryForTest || !selectedDiaryForTest.vocabulary) {
      alert('Không có từ vựng để tạo test!');
      return;
    }

    setIsLoading(true);
    try {
      // Tạo tên file giống như trong table
      const className = getClassName(selectedDiaryForTest.classId);
      const dateStr = formatDate(selectedDiaryForTest.lessonDate).replace(/\//g, '-');
      const fileName = `1 bản 1 mặt-testvocab_${className}_${dateStr}`;

      await dispatch(downloadVocabTest({
        vocabs: selectedDiaryForTest.vocabulary,
        withPhonics: false,
        numberOfStudents,
        fileName
      }));
      setShowVocabTestModal(false);
    } catch (error) {
      console.error('Error downloading vocab test:', error);
      alert('Có lỗi khi tạo file test!');
    } finally {
      setIsLoading(false);
    }
  };

  // Hàm tính toán và điều chỉnh list từ vựng cho flashcard
  const calculateFlashcardVocab = (originalVocab: string, numStudents: number) => {
    const CELLS_PER_PAGE = 32; // 8x4 ô
    const lines = originalVocab.split('\n').filter(line => line.trim() !== '');
    const originalCount = lines.length;

    if (originalCount === 0) return { adjustedVocab: '', sheetsNeeded: 0 };

    // Tìm bội số phù hợp để chia đều
    // Mỗi tờ A4 có 32 ô, cần tìm cách nhân/thêm từ trống để chia đều
    let targetPerSheet = 0;
    let multiplier = 1;
    let studentsPerSheet = 0;

    // Tìm cách chia sao cho mỗi học sinh có đủ từ ban đầu
    // và cắt thẳng ngang được đều
    if (originalCount <= 4) {
      // 4 từ trở xuống: nhân 8 lần = 32 từ, 1 tờ cho 8 học sinh
      targetPerSheet = 4;
      multiplier = 8;
      studentsPerSheet = 8;
    } else if (originalCount <= 8) {
      // 5-8 từ: nhân 4 lần, 1 tờ cho 4 học sinh
      targetPerSheet = 8;
      multiplier = 4;
      studentsPerSheet = 4;
    } else if (originalCount <= 16) {
      // 9-16 từ: nhân 2 lần, 1 tờ cho 2 học sinh
      targetPerSheet = 16;
      multiplier = 2;
      studentsPerSheet = 2;
    } else {
      // 17-32 từ: không nhân, 1 tờ cho 1 học sinh
      targetPerSheet = 32;
      multiplier = 1;
      studentsPerSheet = 1;
    }

    // Thêm từ trống nếu cần để đạt targetPerSheet
    const emptyWordsNeeded = targetPerSheet - originalCount;
    const adjustedLines = [...lines];
    for (let i = 0; i < emptyWordsNeeded; i++) {
      adjustedLines.push('_ : _');
    }

    // Nhân list lên multiplier lần
    let finalLines: string[] = [];
    for (let i = 0; i < multiplier; i++) {
      finalLines = finalLines.concat(adjustedLines);
    }

    // Tính số tờ cần
    const sheetsNeeded = Math.ceil(numStudents / studentsPerSheet);

    return {
      adjustedVocab: finalLines.join('\n'),
      sheetsNeeded
    };
  };

  // Hàm mở modal flashcard
  const handleOpenFlashcardModal = async (diary: any) => {
    setSelectedDiaryForFlashcard(diary);

    // Chuẩn hóa lessonDate về format YYYY-MM-DD
    const lessonDateStr = diary.lessonDate.split('T')[0]; // Lấy phần date nếu có timestamp

    console.log('=== DEBUG FLASHCARD MODAL ===');
    console.log('Diary:', diary);
    console.log('LessonDate:', lessonDateStr);
    console.log('ClassId:', diary.classId);

    // Tìm attendance session của ngày này cho lớp này
    const attendanceSession = attendanceSessions.find((session: any) => {
      const sessionDateStr = typeof session.date === 'string'
        ? session.date.split('T')[0]
        : new Date(session.date).toISOString().split('T')[0];

      return session.classId === diary.classId && sessionDateStr === lessonDateStr;
    });

    console.log('Found attendanceSession:', attendanceSession);

    let numStudents = 0;
    if (attendanceSession) {
      // Load CHỈ records của session này (tối ưu!)
      console.log('Loading attendance records for sessionId:', attendanceSession.id);

      try {
        const records = await dispatch(attendanceStudentRecordActions.getAttendanceStudentRecordBySessionId(attendanceSession.id));
        console.log('Loaded records:', records);

        // Đếm số học sinh đi học (isPresent = true hoặc = 1)
        numStudents = records.filter((record: any) =>
          record.isPresent === true || record.isPresent === 1
        ).length;

        console.log('Number of students present:', numStudents);
      } catch (error) {
        console.error('Error loading attendance records:', error);
      }
    } else {
      console.log('⚠️ No attendance session found for this class and date');
    }

    // Set số học sinh (0 nếu chưa có điểm danh)
    setNumberOfStudentsFlashcard(numStudents);

    // Tính toán và set vocab text
    const { adjustedVocab } = calculateFlashcardVocab(diary.vocabulary || '', numStudents || 1);
    setFlashcardVocabText(adjustedVocab);
    setShowFlashcardModal(true);
  };

  // Hàm download flashcard
  const handleDownloadFlashcard = async () => {
    if (!selectedDiaryForFlashcard || !flashcardVocabText.trim()) {
      alert('Không có từ vựng để tạo flashcard!');
      return;
    }

    if (numberOfStudentsFlashcard === 0) {
      alert('Vui lòng nhập số học sinh cần tạo flashcard!');
      return;
    }

    setIsLoading(true);
    try {
      const className = getClassName(selectedDiaryForFlashcard.classId);
      const dateStr = formatDate(selectedDiaryForFlashcard.lessonDate).replace(/\//g, '-');

      // Tính số tờ cần
      const { sheetsNeeded } = calculateFlashcardVocab(
        selectedDiaryForFlashcard.vocabulary || '',
        numberOfStudentsFlashcard
      );

      const fileName = `${sheetsNeeded} tờ bìa_flashcard_${className}_${dateStr}`;

      await dispatch(submitText({
        vocabs: flashcardVocabText,
        withPhonics: false,
        fileName
      }));
      setShowFlashcardModal(false);
    } catch (error) {
      console.error('Error downloading flashcard:', error);
      alert('Có lỗi khi tạo file flashcard!');
    } finally {
      setIsLoading(false);
    }
  };

  // Hàm mở modal Writing
  const handleOpenWritingModal = async (diary: any) => {
    setSelectedDiaryForWriting(diary);

    // Chuẩn hóa lessonDate về format YYYY-MM-DD
    const lessonDateStr = diary.lessonDate.split('T')[0];

    // Tìm attendance session của ngày này cho lớp này
    const attendanceSession = attendanceSessions.find((session: any) => {
      const sessionDateStr = typeof session.date === 'string'
        ? session.date.split('T')[0]
        : new Date(session.date).toISOString().split('T')[0];

      return session.classId === diary.classId && sessionDateStr === lessonDateStr;
    });

    let numStudents = 0;
    if (attendanceSession) {
      try {
        const records = await dispatch(attendanceStudentRecordActions.getAttendanceStudentRecordBySessionId(attendanceSession.id));
        numStudents = records.filter((record: any) =>
          record.isPresent === true || record.isPresent === 1
        ).length;
      } catch (error) {
        console.error('Error loading attendance records:', error);
      }
    }

    setNumberOfStudentsWriting(numStudents);
    setShowWritingModal(true);
  };

  // Hàm download Writing PDF
  const handleDownloadWriting = async () => {
    if (!selectedDiaryForWriting) {
      alert('Không có dữ liệu để tạo file Writing!');
      return;
    }

    const vocabLines = (selectedDiaryForWriting.vocabulary || '').split('\n').filter((line: string) => line.trim() !== '');
    const sampleLines = (selectedDiaryForWriting.sampleSentences || '').split('\n').filter((line: string) => line.trim() !== '');

    if (vocabLines.length === 0 && sampleLines.length === 0) {
      alert('Không có từ vựng hoặc mẫu câu để tạo file Writing!');
      return;
    }

    if (numberOfStudentsWriting === 0) {
      alert('Vui lòng nhập số học sinh cần tạo file Writing!');
      return;
    }

    setIsLoading(true);
    try {
      const className = getClassName(selectedDiaryForWriting.classId);
      const dateStr = formatDate(selectedDiaryForWriting.lessonDate).replace(/\//g, '-');
      const fileName = `1 bản 1 mặt-writing_${className}_${dateStr}`;

      // Gọi API mới để tạo Writing PDF
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          vocabulary: selectedDiaryForWriting.vocabulary || '',
          sampleSentences: selectedDiaryForWriting.sampleSentences || '',
          numberOfStudents: numberOfStudentsWriting
        })
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/teachers/writing-pdf`, requestOptions);

      if (!response.ok) {
        throw new Error('Failed to generate Writing PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}.pdf`);
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowWritingModal(false);
    } catch (error) {
      console.error('Error downloading Writing:', error);
      alert('Có lỗi khi tạo file Writing!');
    } finally {
      setIsLoading(false);
    }
  };

  // Lấy danh sách schedules của class được chọn
  const getSchedulesForClass = (classId: string) => {
    if (!classId) return [];
    return schedulesList.filter((s: Schedule) => s.classId === Number(classId));
  };

  // Tạo danh sách các buổi học trong tháng hiện tại từ schedulesList
  const generateMonthlySessions = (classId: string, month: number, year: number, excludeDiaryId?: number): MonthlySession[] => {
    if (!classId) return [];

    const schedules = getSchedulesForClass(classId);
    if (schedules.length === 0) return [];

    const daysInMonth = new Date(year, month, 0).getDate();
    const sessions: MonthlySession[] = [];

    // Tạo Set các buổi học đã có nhật ký (trừ nhật ký đang edit)
    const existingDiaries = new Set(
      lessonDiariesList
        .filter((diary: any) =>
          diary.classId === Number(classId) &&
          (!excludeDiaryId || diary.id !== excludeDiaryId)
        )
        .map((diary: any) => `${diary.scheduleId}-${diary.lessonDate}`)
    );

    schedules.forEach((schedule: Schedule) => {
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month - 1, day);
        const jsDay = date.getDay(); // 0 (Sun) to 6 (Sat)
        const convertedDay = jsDay === 0 ? 8 : jsDay + 1; // convert to 2-8 system

        if (schedule.dayOfWeek === convertedDay) {
          const lessonDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const sessionKey = `${schedule.id}-${lessonDate}`;

          // Chỉ thêm vào nếu chưa có nhật ký cho buổi này
          if (!existingDiaries.has(sessionKey)) {
            const displayText = `${convertDayOfWeekToVietnamese(schedule.dayOfWeek)} (Từ ${convertTimeToVietnamese(schedule.startTime, schedule.endTime)}) - Ngày ${day}/${month}`;

            sessions.push({
              scheduleId: schedule.id,
              lessonDate,
              dayOfWeek: schedule.dayOfWeek,
              startTime: schedule.startTime,
              endTime: schedule.endTime,
              displayText
            });
          }
        }
      }
    });

    // Sắp xếp theo ngày
    sessions.sort((a, b) => new Date(a.lessonDate).getTime() - new Date(b.lessonDate).getTime());

    return sessions;
  };

  // Hàm kiểm tra xem form có thay đổi so với trạng thái ban đầu không
  const hasFormChanged = (): boolean => {
    if (!initialFormState) return false;

    // So sánh formData
    if (JSON.stringify(formData) !== JSON.stringify(initialFormState.formData)) return true;

    // So sánh vocabText
    if (vocabText !== initialFormState.vocabText) return true;

    // So sánh sampleSentences
    if (sampleSentences !== initialFormState.sampleSentences) return true;

    // So sánh homework
    if (homework !== initialFormState.homework) return true;

    // So sánh uploadedFiles
    if (uploadedFiles.length !== initialFormState.uploadedFiles.length) return true;

    // So sánh existingFiles
    if (existingFiles.length !== initialFormState.existingFiles.length) return true;
    if (JSON.stringify(existingFiles) !== JSON.stringify(initialFormState.existingFiles)) return true;

    return false;
  };

  // Hàm xử lý đóng modal với kiểm tra thay đổi
  const handleModalClose = () => {
    if (hasFormChanged()) {
      const confirmed = window.confirm(
        'Bạn có thay đổi chưa được lưu. Bạn có chắc chắn muốn hủy và thoát không?'
      );
      if (!confirmed) return;
    }
    setModalOpen(false);
    setInitialFormState(null);
  };

  const handleCreate = () => {
    setEditingDiary(null);

    // Auto select class if filtering by a specific class
    const autoSelectedClassId = selectedClassFilter !== null ? selectedClassFilter.toString() : '';

    // Reset modal month/year to current
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    setModalMonth(currentMonth);
    setModalYear(currentYear);

    const initialData = {
      classId: autoSelectedClassId,
      scheduleId: '',
      lessonDate: '',
      contentPlanned: '',
      contentTaught: '',
      note: '',
    };

    setFormData(initialData);
    setVocabText('');
    setSampleSentences('');
    setHomework('');
    setUploadedFiles([]);
    setExistingFiles([]);
    setRemovedFiles([]);

    // Lưu trạng thái ban đầu
    setInitialFormState({
      formData: initialData,
      vocabText: '',
      sampleSentences: '',
      homework: '',
      uploadedFiles: [],
      existingFiles: [],
    });

    // Generate monthly sessions if a class is auto-selected
    if (autoSelectedClassId) {
      const sessions = generateMonthlySessions(autoSelectedClassId, currentMonth, currentYear);
      setMonthlySessions(sessions);
    } else {
      setMonthlySessions([]);
    }

    setModalOpen(true);
  };

  const handleEdit = (diary: any) => {
    // Close note modal if it was open to avoid double overlays
    setShowNoteModal(false);
    setEditingDiary(diary);

    const initialData = {
      classId: diary.classId.toString(),
      scheduleId: diary.scheduleId.toString(),
      lessonDate: diary.lessonDate,
      contentPlanned: diary.contentPlanned || '',
      contentTaught: diary.contentTaught || '',
      note: diary.note || '',
    };

    setFormData(initialData);

    const initialVocab = diary.vocabulary || '';
    const initialSamples = diary.sampleSentences || '';
    const initialHomework = diary.homework || '';
    const initialExisting = diary.attachments || [];

    setVocabText(initialVocab);
    setSampleSentences(initialSamples);
    setHomework(initialHomework);
    setUploadedFiles([]);
    setExistingFiles(initialExisting);
    setRemovedFiles([]);

    // Lưu trạng thái ban đầu
    setInitialFormState({
      formData: initialData,
      vocabText: initialVocab,
      sampleSentences: initialSamples,
      homework: initialHomework,
      uploadedFiles: [],
      existingFiles: [...initialExisting],
    });

    // Generate monthly sessions for edit mode (exclude current diary)
    // Use the month/year from the diary being edited
    const diaryDate = new Date(diary.lessonDate);
    const editMonth = diaryDate.getMonth() + 1;
    const editYear = diaryDate.getFullYear();
    setModalMonth(editMonth);
    setModalYear(editYear);
    const sessions = generateMonthlySessions(diary.classId.toString(), editMonth, editYear, diary.id);
    setMonthlySessions(sessions);

    setModalOpen(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa nhật ký này?')) {
      dispatch(lessonDiaryActions.deleteLessonDiary(id));
    }
  };

  const handleSave = async () => {
    if (!formData.classId || !formData.scheduleId || !formData.lessonDate) {
      alert('Vui lòng chọn lớp và buổi học!');
      return;
    }

    setIsLoading(true);
    const data = new FormData();
    data.append('classId', formData.classId);
    data.append('scheduleId', formData.scheduleId);
    data.append('lessonDate', formData.lessonDate);
    data.append('contentPlanned', formData.contentPlanned);
    data.append('contentTaught', formData.contentTaught);
    data.append('vocabulary', vocabText);
    data.append('sampleSentences', sampleSentences);
    data.append('homework', homework);
    data.append('note', formData.note);

    // Append new files
    uploadedFiles.forEach((file) => {
      data.append('files', file);
    });

    if (editingDiary) {
      // Append removed files for update
      data.append('removedFiles', JSON.stringify(removedFiles));
      await dispatch(lessonDiaryActions.updateLessonDiary(editingDiary.id, data));
    } else {
      await dispatch(lessonDiaryActions.createLessonDiary(data));
    }

    // Reload dữ liệu sau khi tạo/cập nhật thành công
    await dispatch(lessonDiaryActions.getByMonth(selectedYear, selectedMonth));

    setIsLoading(false);
    setModalOpen(false);
    setInitialFormState(null); // Reset initial state sau khi lưu thành công
  };

  // Note quick edit modal state
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteModalDiary, setNoteModalDiary] = useState<any>(null);
  const [noteModalValue, setNoteModalValue] = useState<string>("");

  const openNoteModal = (diary: any) => {
    setNoteModalDiary(diary);
    setNoteModalValue(diary.note || "");
    // Ensure other modals are closed so only note modal is visible
    setModalOpen(false);
    setShowVocabViewModal(false);
    setShowSampleSentencesViewModal(false);
    setShowNoteModal(true);
  };

  const closeNoteModal = () => {
    setShowNoteModal(false);
    setNoteModalDiary(null);
  };

  const saveNoteQuickEdit = async () => {
    if (!noteModalDiary) return;
    const d = noteModalDiary;
    setIsLoading(true);
    const data = new FormData();
    // Preserve existing fields to satisfy update endpoint
    data.append('classId', String(d.classId));
    data.append('scheduleId', String(d.scheduleId));
    data.append('lessonDate', d.lessonDate);
    data.append('contentPlanned', d.contentPlanned || '');
    data.append('contentTaught', d.contentTaught || '');
    data.append('vocabulary', d.vocabulary || '');
    data.append('sampleSentences', d.sampleSentences || '');
    data.append('homework', d.homework || '');
    data.append('note', noteModalValue || '');
    // No file changes in quick edit
    data.append('removedFiles', JSON.stringify([]));

    await dispatch(lessonDiaryActions.updateLessonDiary(d.id, data));
    await dispatch(lessonDiaryActions.getByMonth(selectedYear, selectedMonth));
    setIsLoading(false);
    setShowNoteModal(false);
    setNoteModalDiary(null);
  };

  const handleVocabTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setVocabText(e.target.value);
  };

  const countNonEmptyLines = (text: string) => {
    return text.split('\n').filter(line => line.trim() !== '').length;
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB in bytes

      // Kiểm tra kích thước từng file
      const oversizedFiles = newFiles.filter(file => file.size > MAX_FILE_SIZE);

      if (oversizedFiles.length > 0) {
        const fileNames = oversizedFiles.map(f => f.name).join(', ');
        alert(`Các file sau vượt quá giới hạn 200MB:\n${fileNames}\n\nVui lòng liên hệ Admin để được hỗ trợ upload file lớn hơn.`);
        e.target.value = '';
        return;
      }

      setUploadedFiles(prev => [...prev, ...newFiles]);
      e.target.value = '';
    }
  };

  const handleRemoveNewFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingFile = (filename: string) => {
    setExistingFiles(prev => prev.filter(f => f !== filename));
    setRemovedFiles(prev => [...prev, filename]);
  };

  const handleDownloadFile = (filename: string, originalFileName: string) => {
    const downloadUrl = `${process.env.REACT_APP_API_URL}/api/lesson_diaries/download/${filename}`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = originalFileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleChooseFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (e.dataTransfer.files) {
      const newFiles = Array.from(e.dataTransfer.files);
      const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB in bytes

      // Kiểm tra kích thước từng file
      const oversizedFiles = newFiles.filter(file => file.size > MAX_FILE_SIZE);

      if (oversizedFiles.length > 0) {
        const fileNames = oversizedFiles.map(f => f.name).join(', ');
        alert(`Các file sau vượt quá giới hạn 200MB:\n${fileNames}\n\nVui lòng liên hệ Admin để được hỗ trợ upload file lớn hơn.`);
        return;
      }

      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  };

  // Hàm copy từ vựng
  const handleCopyVocabulary = (vocabulary: string) => {
    navigator.clipboard.writeText(vocabulary)
      .catch((err) => {
        console.error('Lỗi khi copy:', err);
      });
  };

  const handleCopySampleSentences = (sampleSentences: string) => {
    navigator.clipboard.writeText(sampleSentences)
      .catch((err) => {
        console.error('Lỗi khi copy:', err);
      });
  };

  // Hàm tạo flashcard set từ diary
  const navigate = useNavigate();

  const handleCreateFlashcardSet = async (diaryId: number) => {
    try {
      setCreatingFlashcardSetId(diaryId);
      const result = await dispatch(lessonDiaryActions.createFlashcardSet(diaryId));
      if (result?.success) {
        // Reload để cập nhật flashcardSetId
        await dispatch(lessonDiaryActions.getByMonth(selectedYear, selectedMonth));
        alert('✅ Tạo bộ thẻ từ thành công!');
      } else {
        alert('❌ Lỗi: ' + (result?.message || 'Không thể tạo bộ thẻ từ'));
      }
    } catch (error: any) {
      console.error('Error creating flashcard set:', error);
      alert('❌ Lỗi khi tạo bộ thẻ từ: ' + (error?.message || error));
    } finally {
      setCreatingFlashcardSetId(null);
    }
  };

  const handleReviewFlashcard = (flashcardSetId: number) => {
    navigate(`/flashcards/sets/${flashcardSetId}/detail`);
  };

  // Kiểm tra một chuỗi có chứa dấu tiếng Việt không
  const hasVietnamese = (s: string) =>
    /[àáảãạăắặằẳẵâấầẩẫậèéẻẽẹêếềểễệìíỉĩịòóỏõọôốồổỗộơớờởỡợùúủũụưứừửữựỳýỷỹỵđÀÁẢÃẠĂẮẶẰẲẴÂẤẦẨẪẬÈÉẺẼẸÊẾỀỂỄỆÌÍỈĨỊÒÓỎÕỌÔỐỒỔỖỘƠỚỜỞỠỢÙÚỦŨỤƯỨỪỬỮỰỲÝỶỸỴĐ]/.test(s);

  // Kiểm tra xem mẫu câu có ít nhất 1 cặp hợp lệ không
  const hasSentenceFormat = (text: string): boolean => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      // Định dạng 1 dòng với dấu mũi tên
      if (line.includes('→') && line.indexOf('→') > 0) return true;
      // Định dạng 1 dòng với dấu hai chấm (colon)
      const colonIdx = line.indexOf(':');
      if (colonIdx > 0 && colonIdx < line.length - 1) return true;
      // Định dạng 2 dòng: dòng tiếng Anh phải KHÔNG có dấu tiếng Việt
      const english = line.replace(/^\d+\.\s*/, '').trim();
      if (english && !hasVietnamese(english) && i + 1 < lines.length) {
        const nextLine = lines[i + 1];
        const isNextSingleLine = nextLine.includes('→') && nextLine.indexOf('→') > 0;
        const nextColonIdx = nextLine.indexOf(':');
        const isNextColon = nextColonIdx > 0 && nextColonIdx < nextLine.length - 1;
        if (!isNextSingleLine && !isNextColon) {
          // Dòng sau không phải single-line → đây là cặp 2 dòng hợp lệ
          return true;
        }
      }
    }
    return false;
  };

  // Hàm tạo trang ôn tập từ diary
  const handleCreateReviewSession = async (diaryId: number) => {
    // Lấy diary tương ứng từ Redux store
    const diary = lessonDiariesList.find((d: any) => d.id === diaryId);

    // Kiểm tra mẫu câu: nếu có text nhưng không dòng nào hợp lệ → hiện modal cảnh báo
    const sampleText = (diary?.sampleSentences || '').trim();
    if (sampleText && !hasSentenceFormat(sampleText)) {
      setShowSentenceFormatModal(true);
      return;
    }

    try {
      setCreatingReviewId(diaryId);
      const result = await dispatch(reviewSessionActions.createFromDiary(diaryId));
      if (result?.success) {
        await dispatch(lessonDiaryActions.getByMonth(selectedYear, selectedMonth));
      }
    } catch {
      // Error notification is dispatched inside the action creator
    } finally {
      setCreatingReviewId(null);
    }
  };

  const handleCopyReviewLink = (reviewSessionId: number) => {
    const link = `${window.location.origin}/review/${reviewSessionId}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedReviewId(reviewSessionId);
      setTimeout(() => setCopiedReviewId(null), 2000);
    });
  };

  // Hàm mở modal xem từ vựng
  const handleViewVocabulary = (vocabulary: string, className: string, diary?: any) => {
    setViewModalContent(vocabulary);
    setViewModalTitle(`Từ vựng - ${className}`);
    setViewModalDiary(diary || null);
    setShowVocabViewModal(true);
  };

  // Hàm mở modal xem mẫu câu
  const handleViewSampleSentences = (sampleSentences: string, className: string) => {
    setViewModalContent(sampleSentences);
    setViewModalTitle(`Mẫu câu - ${className}`);
    setShowSampleSentencesViewModal(true);
  };

  // Hàm cắt ngắn tên file nếu quá dài
  const truncateFileName = (fileName: string, maxLength: number = 30) => {
    if (fileName.length <= maxLength) return fileName;

    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const extLength = extension ? extension.length + 1 : 0;
    const availableLength = maxLength - extLength - 3; // 3 for "..."

    if (availableLength <= 0) return fileName;

    const firstPart = nameWithoutExt.substring(0, Math.ceil(availableLength / 2));
    const lastPart = nameWithoutExt.substring(nameWithoutExt.length - Math.floor(availableLength / 2));

    return `${firstPart}...${lastPart}${extension ? '.' + extension : ''}`;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Reset scheduleId và lessonDate khi đổi class
    if (name === 'classId') {
      setFormData(prev => ({ ...prev, scheduleId: '', lessonDate: '' }));
      // Generate monthly sessions cho class mới
      const sessions = generateMonthlySessions(value, modalMonth, modalYear, editingDiary?.id);
      setMonthlySessions(sessions);
    }

    // Khi chọn session, cập nhật scheduleId và lessonDate
    if (name === 'sessionSelect') {
      const selectedSession = monthlySessions.find(s => `${s.scheduleId}-${s.lessonDate}` === value);
      if (selectedSession) {
        setFormData(prev => ({
          ...prev,
          scheduleId: selectedSession.scheduleId.toString(),
          lessonDate: selectedSession.lessonDate
        }));
      }
    }
  };

  // Handler for modal month/year changes
  const handleModalMonthYearChange = (newMonth: number, newYear: number) => {
    setModalMonth(newMonth);
    setModalYear(newYear);

    // Recalculate sessions if a class is selected
    if (formData.classId) {
      setFormData(prev => ({ ...prev, scheduleId: '', lessonDate: '' }));
      const sessions = generateMonthlySessions(formData.classId, newMonth, newYear, editingDiary?.id);
      setMonthlySessions(sessions);
    }
  };

  const convertDayOfWeekToVietnamese = (dayOfWeek: number) => {
    const days = ['', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy', 'Chủ Nhật'];
    return days[dayOfWeek - 1];
  };

  const convertTimeToVietnamese = (start: string, end: string) => {
    if (!start || !end) return '';
    const formatTime = (time: string) => {
      const [hourStr, minuteStr] = time.split(':');
      const hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);
      if (isNaN(hour) || isNaN(minute)) return '';
      const formattedHour = hour < 12 ? hour : hour - 12;
      return minute === 0 ? `${formattedHour}h` : `${formattedHour}h${minute < 10 ? `0${minute}` : minute}`;
    };
    return `${formatTime(start)} -> ${formatTime(end)}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getClassName = (classId: number) => {
    if (classId === null || classId === undefined || !classes || classes.length === 0) return 'N/A';
    const cls = classes.find((c: any) => Number(c.id) === Number(classId));
    return cls ? cls.name : `Class ID: ${classId}`;
  };

  const getScheduleLabel = (scheduleId: number, diary?: any) => {
    // Ưu tiên snapshot lưu trực tiếp trong nhật kí
    if (diary?.sessionDayOfWeek != null && diary?.sessionStartTime) {
      return `${convertDayOfWeekToVietnamese(diary.sessionDayOfWeek)} (${convertTimeToVietnamese(diary.sessionStartTime, diary.sessionEndTime)})`;
    }
    // Fallback: tìm trong schedulesList (record cũ chưa có snapshot)
    if (!scheduleId || !schedulesList || schedulesList.length === 0) return 'N/A';
    const schedule = schedulesList.find((s: Schedule) => s.id === scheduleId);
    if (!schedule) return 'N/A';
    return `${convertDayOfWeekToVietnamese(schedule.dayOfWeek)} (${convertTimeToVietnamese(schedule.startTime, schedule.endTime)})`;
  };

  // Lọc và sắp xếp danh sách nhật ký
  const filteredAndSortedDiaries = lessonDiariesList
    .filter((diary: any) => {
      // Filter theo lớp
      if (selectedClassFilter === null) {
        // Nếu không chọn lớp cụ thể, kiểm tra filter theo teacher
        if (selectedTeacherFilter !== null) {
          // Lọc các lớp mà teacher này chủ nhiệm
          const teacherClasses = classes.filter((cls: any) => cls.teacherId === selectedTeacherFilter);
          const teacherClassIds = teacherClasses.map((cls: any) => cls.id);
          return teacherClassIds.includes(diary.classId);
        }
        return true;
      } else {
        // Nếu đã chọn lớp cụ thể, chỉ lọc theo lớp đó
        return diary.classId === selectedClassFilter;
      }
    })
    .sort((a: any, b: any) => {
      // Sắp xếp theo createdAt giảm dần (mới nhất lên đầu)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  return (
    <PageWrapper>
      {/* Luôn hiển thị content - FadeIn animation sẽ tạo hiệu ứng loading mượt mà */}
      <HeaderSection>
        {/* Dòng đầu tiên: Nút Tạo + Text Tháng/Năm */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <CreateButton onClick={handleCreate}>
              + Tạo Nhật ký
            </CreateButton>
            <button
              onClick={handleOpenDownloadModal}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 8,
                border: '1.5px solid #6366f1',
                background: 'rgba(99,102,241,0.08)',
                color: '#6366f1', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', transition: 'all 0.18s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.16)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; }}
            >
              📥 Tải nhật kí
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontWeight: '600', fontSize: '15px' }}>Nhật ký Tháng</span>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              style={{ width: '80px' }}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </Select>
            <span style={{ fontWeight: '600', fontSize: '15px' }}>Năm</span>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              style={{ width: '100px' }}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Select>

            {/* Dropdown filter theo giáo viên - chỉ hiện với Admin */}
            {isAdmin && (
              <>
                <span style={{ fontWeight: '600', fontSize: '15px', marginLeft: '15px' }}>Giáo viên</span>
                <Select
                  value={selectedTeacherFilter === null ? '' : selectedTeacherFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedTeacherFilter(value === '' ? null : Number(value));
                    // Reset class filter khi chọn teacher
                    setSelectedClassFilter(null);
                  }}
                  style={{ width: '150px' }}
                >
                  <option value="">Tất cả</option>
                  {teachers
                    .slice()
                    .sort((a: any, b: any) => a.userName.localeCompare(b.userName))
                    .map((teacher: any) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.userName}
                      </option>
                    ))}
                </Select>
              </>
            )}
          </div>
        </div>

        {/* Dòng thứ hai: Filter tags */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {/* Tag "Tất cả" */}
          <FilterTag
            isActive={selectedClassFilter === null}
            onClick={() => setSelectedClassFilter(null)}
          >
            Tất cả
          </FilterTag>

          {classes
            .slice()
            .sort((a: any, b: any) => a.name.localeCompare(b.name))
            .map((cls: any) => (
              <FilterTag
                key={cls.id}
                isActive={selectedClassFilter === cls.id}
                onClick={() => setSelectedClassFilter(selectedClassFilter === cls.id ? null : cls.id)}
              >
                {cls.name}
              </FilterTag>
            ))}
        </div>
      </HeaderSection>

      <TableContainer>
        <StyledTable>
          <thead>
            <tr>
              <th>Ca học</th>
              <th>Soạn bài</th>
              <th>Từ vựng/ Thẻ từ</th>
              <th>Nội dung đã dạy/ BTVN</th>
              <th>File Tài liệu</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedDiaries.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  {selectedClassFilter ? 'Không có nhật ký nào cho lớp này.' : 'Chưa có nhật ký nào. Hãy tạo nhật ký mới!'}
                </td>
              </tr>
            ) : (
              filteredAndSortedDiaries.map((diary: any) => {
                const createdTeacherName = teachers && teachers.length > 0
                  ? (teachers.find((teacher: any) => teacher.id === diary.createdByTeacherId)?.userName || 'N/A')
                  : 'N/A';
                const lastUpdatedTeacherName = teachers && teachers.length > 0
                  ? (teachers.find((teacher: any) => teacher.id === diary.lastUpdatedByTeacherId)?.userName || 'N/A')
                  : 'N/A';

                // Check xem đã sửa chưa bằng cách so sánh createdAt với updatedAt
                const isEdited = diary.createdAt !== diary.updatedAt;

                // Format thời gian: DD/MM/YYYY HH:mm
                const formatDateTime = (dateString: string) => {
                  const date = new Date(dateString);
                  const day = date.getDate().toString().padStart(2, '0');
                  const month = (date.getMonth() + 1).toString().padStart(2, '0');
                  const year = date.getFullYear();
                  const hours = date.getHours().toString().padStart(2, '0');
                  const minutes = date.getMinutes().toString().padStart(2, '0');
                  return `${day}/${month}/${year} ${hours}:${minutes}`;
                };

                // Đếm số từ vựng
                const vocabCount = diary.vocabulary
                  ? diary.vocabulary.split('\n').filter((line: string) => line.trim() !== '').length
                  : 0;

                // Đếm số mẫu câu
                const sampleSentencesCount = diary.sampleSentences
                  ? diary.sampleSentences.split('\n').filter((line: string) => line.trim() !== '').length
                  : 0;

                return (
                  <tr key={diary.id}>
                    <td>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {getClassName(diary.classId)}
                      </div>
                      <div style={{ fontSize: '12px' }}>
                        {(() => {
                          // Ưu tiên snapshot, fallback sang schedulesList
                          const dow = diary.sessionDayOfWeek ?? schedulesList.find((s: Schedule) => s.id === diary.scheduleId)?.dayOfWeek;
                          if (dow == null) return 'N/A';
                          return `${convertDayOfWeekToVietnamese(dow)}, ${formatDate(diary.lessonDate)}`;
                        })()}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {(() => {
                          const st = diary.sessionStartTime ?? schedulesList.find((s: Schedule) => s.id === diary.scheduleId)?.startTime;
                          const et = diary.sessionEndTime ?? schedulesList.find((s: Schedule) => s.id === diary.scheduleId)?.endTime;
                          if (!st) return '';
                          return `(${convertTimeToVietnamese(st, et)})`;
                        })()}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '6px',
                            padding: '8px 12px',
                            backgroundColor: '#e6fff5',
                            borderRadius: '6px',
                            border: '1px solid #80e5c2',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          <span style={{ fontSize: '12px', color: '#0f766e', fontWeight: '500' }}>
                            ✍️ Nội dung đã soạn
                          </span>
                          <div style={{ color: '#065f46' }}>{diary.contentPlanned || 'N/A'}</div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '6px',
                            padding: '8px 12px',
                            backgroundColor: '#ffecec',
                            borderRadius: '6px',
                            border: '1px solid #ffb3b3',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            cursor: 'pointer'
                          }}
                          onClick={() => openNoteModal(diary)}
                        >
                          <span style={{ fontSize: '12px', color: '#cc0000', fontWeight: '500' }}>
                            📝 Ghi chú (bấm để sửa)
                          </span>
                          <div style={{ color: '#660000' }}>{diary.note || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {(vocabCount > 0 || sampleSentencesCount > 0) && (
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          {vocabCount > 0 && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: '6px',
                                padding: '6px 10px',
                                backgroundColor: '#e7f3ff',
                                borderRadius: '6px',
                                border: '1px solid #b3d9ff',
                                flex: 1,
                                cursor: 'pointer'
                              }}
                              onClick={() => handleViewVocabulary(diary.vocabulary, getClassName(diary.classId), diary)}
                            >
                              <span style={{ fontSize: '12px', color: '#0066cc', fontWeight: '500' }}>
                                📚 {vocabCount} Từ vựng
                              </span>
                              {/* Buttons moved to modal */}
                            </div>
                          )}

                          {sampleSentencesCount > 0 && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: '6px',
                                padding: '6px 10px',
                                backgroundColor: '#fff3e0',
                                borderRadius: '6px',
                                border: '1px solid #ffcc80',
                                flex: 1,
                                cursor: 'pointer'
                              }}
                              onClick={() => handleViewSampleSentences(diary.sampleSentences, getClassName(diary.classId))}
                            >
                              <span style={{ fontSize: '12px', color: '#e65100', fontWeight: '500' }}>
                                💬 {sampleSentencesCount} Mẫu câu
                              </span>
                              {/* Copy button moved to modal */}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Review session standalone buttons — outside boxes */}
                      {(vocabCount > 0 || sampleSentencesCount > 0) && (
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                          {(diary as any).reviewSessionId ? (
                            <SuccessButton
                              onClick={() => navigate(`/review/${(diary as any).reviewSessionId}`)}
                              title="Xem bài ôn tập"
                              style={{ background: 'linear-gradient(135deg, #72f0c6ff, #04ba80ff)', fontSize: '12px', padding: '4px 10px' }}
                            >
                              📖 Xem ôn tập (ID: {(diary as any).reviewSessionId})
                            </SuccessButton>
                          ) : (
                            <PrimaryButton
                              onClick={() => handleCreateReviewSession(diary.id)}
                              title="Tạo bài ôn tập"
                              disabled={creatingReviewId === diary.id}
                              style={{ background: 'linear-gradient(135deg, #76cef0ff, #028ecaff)', fontSize: '12px', padding: '4px 10px' }}
                            >
                              {creatingReviewId === diary.id ? '⏳ Đang tạo...' : '📖 Tạo ôn tập'}
                            </PrimaryButton>
                          )}
                        </div>
                      )}


                      {vocabCount > 0 && (
                        <>
                          <VocabTestBox>
                            <span style={{ fontSize: '12px', color: '#cc6600', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                              📝 testvocab_{getClassName(diary.classId)}_{formatDate(diary.lessonDate).replace(/\//g, '-')}
                            </span>
                            <FileDownloadButton
                              onClick={() => handleOpenVocabTestModal(diary)}
                              title="Tải test từ vựng"
                              style={{ flexShrink: 0 }}
                            >
                              ↓
                            </FileDownloadButton>
                          </VocabTestBox>

                          <FileItemBox>
                            <span style={{ fontSize: '12px', color: '#28a745', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                              📇 flashcard_{getClassName(diary.classId)}_{formatDate(diary.lessonDate).replace(/\//g, '-')}
                            </span>
                            <FileDownloadButton
                              onClick={() => handleOpenFlashcardModal(diary)}
                              title="Tải flashcard"
                              style={{ flexShrink: 0 }}
                            >
                              ↓
                            </FileDownloadButton>
                          </FileItemBox>

                          <FileItemBox>
                            <span style={{ fontSize: '12px', color: '#007bff', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                              📄 writing_{getClassName(diary.classId)}_{formatDate(diary.lessonDate).replace(/\//g, '-')}
                            </span>
                            <FileDownloadButton
                              onClick={() => handleOpenWritingModal(diary)}
                              title="Tải file Writing"
                              style={{ flexShrink: 0 }}
                            >
                              ↓
                            </FileDownloadButton>
                          </FileItemBox>
                        </>
                      )}
                    </td>
                    <td>
                      {diary.contentTaught && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '6px',
                            padding: '8px 12px',
                            backgroundColor: '#e8f5e9',
                            borderRadius: '6px',
                            border: '1px solid #81c784',
                            marginBottom: '8px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          <span style={{ fontSize: '12px', color: '#2e7d32', fontWeight: '500' }}>
                            ✓ Đã dạy:
                          </span>
                          <div style={{ color: '#1b5e20' }}>{diary.contentTaught}</div>
                        </div>
                      )}
                      {diary.homework && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '6px',
                            padding: '8px 12px',
                            backgroundColor: '#fff3e0',
                            borderRadius: '6px',
                            border: '1px solid #ffb74d',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          <span style={{ fontSize: '12px', color: '#e65100', fontWeight: '500' }}>
                            📝 BTVN:
                          </span>
                          <div style={{ color: '#bf360c' }}>{diary.homework}</div>
                        </div>
                      )}
                      {!diary.contentTaught && !diary.homework && (
                        <div style={{ color: '#999', fontSize: '12px' }}>N/A</div>
                      )}
                    </td>
                    <td>
                      {diary.attachments && diary.attachments.length > 0 ? (
                        <div>
                          {diary.attachments.map((filename: string, index: number) => {
                            const originalFileName = decodeURIComponent(filename.split('-').slice(2).join('-'));
                            const displayFileName = truncateFileName(originalFileName);
                            return (
                              <FileItemBox key={index}>
                                <span style={{ fontSize: '12px', color: '#28a745', fontWeight: '500' }}>
                                  📄
                                </span>
                                <FileNameSpan title={originalFileName}>
                                  {displayFileName}
                                </FileNameSpan>
                                <FileDownloadButton
                                  onClick={() => handleDownloadFile(filename, originalFileName)}
                                  title="Tải xuống"
                                  style={{ flexShrink: 0 }}
                                >
                                  ↓
                                </FileDownloadButton>
                              </FileItemBox>
                            );
                          })}
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontSize: '12px' }}>Không có file</span>
                      )}
                    </td>
                    <td>
                      <div>
                        <WarningButton onClick={() => handleEdit(diary)}>
                          SỬA
                        </WarningButton>
                        {' '}
                        <DangerButton onClick={() => handleDelete(diary.id)}>
                          XOÁ
                        </DangerButton>
                      </div>
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '8px', borderTop: '1px solid #eee', paddingTop: '8px' }}>
                        <div>
                          <strong>Tạo:</strong> {createdTeacherName}
                        </div>
                        <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                          {formatDateTime(diary.createdAt)}
                        </div>
                        {isEdited && (
                          <>
                            <div style={{ marginTop: '6px' }}>
                              <strong>Sửa:</strong> {lastUpdatedTeacherName}
                            </div>
                            <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                              {formatDateTime(diary.updatedAt)}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </StyledTable>
      </TableContainer>

      {/* Modal Form */}
      <Modal isOpen={modalOpen} toggle={handleModalClose} size="xl" style={{ maxWidth: '95vw', width: '1400px' }}>
        <ModalHeader toggle={handleModalClose}>
          {editingDiary ? 'Sửa Nhật ký' : 'Tạo Nhật ký'}
        </ModalHeader>
        <ModalBody style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {/* Row 1: Class, Session, Today */}
          <FormRowGroup style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
            <FormRow style={{ flex: '1', minWidth: '150px' }}>
              <StyledLabel>Lớp học</StyledLabel>
              <Select name="classId" value={formData.classId} onChange={handleInputChange} style={{ width: '100%' }}>
                <option value="">Chọn lớp</option>
                {classes.map((cls: any) => (
                  <option key={cls.id} value={cls.id}>{cls.name}</option>
                ))}
              </Select>
            </FormRow>

            <FormRow style={{ flex: '2', minWidth: '250px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <StyledLabel style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Ca học</StyledLabel>

                {/* Month/Year Selectors - Compact */}
                <Select
                  value={modalMonth}
                  onChange={(e) => handleModalMonthYearChange(Number(e.target.value), modalYear)}
                  style={{ width: '90px' }}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={month}>Th {month}</option>
                  ))}
                </Select>

                <Select
                  value={modalYear}
                  onChange={(e) => handleModalMonthYearChange(modalMonth, Number(e.target.value))}
                  style={{ width: '85px' }}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </Select>
              </div>

              {/* Session Selector */}
              <Select
                name="sessionSelect"
                value={formData.scheduleId && formData.lessonDate ? `${formData.scheduleId}-${formData.lessonDate}` : ''}
                onChange={handleInputChange}
                disabled={!formData.classId || monthlySessions.length === 0}
                style={{ width: '100%' }}
              >
                <option value="">Chọn ca học</option>
                {monthlySessions.map((session, index) => (
                  <option key={index} value={`${session.scheduleId}-${session.lessonDate}`}>
                    {session.displayText}
                  </option>
                ))}
              </Select>
              {formData.classId && monthlySessions.length === 0 && (
                <div style={{ fontSize: '12px', color: '#dc3545', marginTop: '4px' }}>
                  {editingDiary
                    ? 'Tất cả các buổi học trong tháng đã có nhật ký.'
                    : 'Lớp này chưa có lịch học hoặc tất cả buổi học đã có nhật ký.'}
                </div>
              )}
            </FormRow>

            <FormRow style={{ flex: '1', minWidth: '150px' }}>
              <StyledLabel>Hôm nay ngày</StyledLabel>
              <Input
                type="text"
                value={new Date().toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                disabled
                style={{
                  width: '100%',
                  backgroundColor: '#f0f0f0',
                  cursor: 'not-allowed',
                  color: '#666'
                }}
              />
            </FormRow>
          </FormRowGroup>

          {/* Row 2: Soạn bài & Nội dung đã dạy */}
          <FormRowGroup style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
            <FormRow style={{ flex: 1 }}>
              <StyledLabel>Soạn bài</StyledLabel>
              <TextArea name="contentPlanned" rows={4} placeholder="Nhập nội dung soạn bài..." value={formData.contentPlanned} onChange={handleInputChange} />
            </FormRow>

            <FormRow style={{ flex: 1 }}>
              <StyledLabel>Nội dung đã dạy</StyledLabel>
              <TextArea name="contentTaught" rows={4} placeholder="Nhập nội dung đã dạy..." value={formData.contentTaught} onChange={handleInputChange} />
            </FormRow>
          </FormRowGroup>

          {/* Row 3: File đính kèm */}
          <FormRow style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
              {/* Left side: Label, button, and drop zone */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '300px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <StyledLabel style={{ marginBottom: 0 }}>Tài liệu dạy học đính kèm</StyledLabel>
                  <span style={{ fontSize: '12px', fontStyle: 'italic', color: '#666' }}>
                    (.pdf, .docx, file ảnh ....)
                  </span>
                </div>

                {/* Drop zone */}
                <div
                  ref={dropZoneRef}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  style={{
                    border: isDragOver ? '2px solid #007bff' : '2px dashed #ccc',
                    borderRadius: '8px',
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: isDragOver ? '#f0f7ff' : '#f9f9f9',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div style={{ fontSize: '14px', color: isDragOver ? '#007bff' : '#666', fontWeight: '500' }}>
                    📁 Kéo thả file vào đây
                  </div>
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
                    hoặc
                  </div>
                  <ChooseFileButton type="button" onClick={handleChooseFileClick} style={{ marginTop: '12px' }}>
                    Chọn file
                  </ChooseFileButton>
                </div>
              </div>

              {/* Right side: File list - only show when has files */}
              {(existingFiles.length > 0 || uploadedFiles.length > 0) && (
                <div style={{
                  flex: 1
                }}>
                  <FileListContainer style={{ marginTop: 0 }}>
                    {existingFiles.map((filename, index) => {
                      const originalFileName = decodeURIComponent(filename.split('-').slice(2).join('-'));

                      return (
                        <FileListItem key={`existing-${index}`}>
                          <span>{originalFileName}</span>
                          <FileDownloadButton
                            onClick={() => handleDownloadFile(filename, originalFileName)}
                            title="Tải xuống"
                          >
                            ↓
                          </FileDownloadButton>
                          <FileRemoveButton onClick={() => handleRemoveExistingFile(filename)}>
                            ✕
                          </FileRemoveButton>
                        </FileListItem>
                      );
                    })}
                    {uploadedFiles.map((file, index) => (
                      <FileListItem key={`new-${index}`}>
                        <span>{file.name}</span>
                        <FileRemoveButton onClick={() => handleRemoveNewFile(index)}>
                          ✕
                        </FileRemoveButton>
                      </FileListItem>
                    ))}
                  </FileListContainer>
                </div>
              )}
            </div>
            <FileInput
              type="file"
              multiple
              onChange={handleFileChange}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
          </FormRow>

          {/* Row 4: Từ vựng, Mẫu câu, BTVN - 3 cột */}
          <FormRowGroup style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
            <FormRow style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <StyledLabel>Từ vựng</StyledLabel>
                <VocabCount>Tổng số từ: {countNonEmptyLines(vocabText)}</VocabCount>
              </div>
              <TextArea
                rows={5}
                placeholder="Mỗi từ và nghĩa nằm trên 1 hàng, ngăn cách nhau bởi dấu hai chấm (:)&#10;Ví dụ:&#10;apple: quả táo&#10;book: quyển sách"
                value={vocabText}
                onChange={handleVocabTextChange}
                ref={textareaRef}
              />
              {invalidLines.length > 0 && (
                <div style={{
                  padding: '8px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '4px',
                  marginTop: '6px',
                  fontSize: '12px'
                }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '4px', color: '#856404' }}>
                    Các dòng sau không hợp lệ:
                  </p>
                  {invalidLines.slice(0, 3).map((line, index) => (
                    <p key={index} style={{ color: 'red', marginBottom: '3px' }}>{line}</p>
                  ))}
                  {invalidLines.length > 3 && <p style={{ color: '#856404' }}>... và {invalidLines.length - 3} dòng khác</p>}
                </div>
              )}
            </FormRow>

            <FormRow style={{ flex: 1 }}>
              <StyledLabel>Mẫu câu (Nếu có)</StyledLabel>
              <TextArea
                rows={5}
                placeholder="Nhập mẫu câu..."
                value={sampleSentences}
                onChange={(e) => setSampleSentences(e.target.value)}
              />
            </FormRow>

            <FormRow style={{ flex: 1 }}>
              <StyledLabel>BTVN</StyledLabel>
              <TextArea
                rows={5}
                placeholder="Nhập bài tập về nhà..."
                value={homework}
                onChange={(e) => setHomework(e.target.value)}
              />
            </FormRow>
          </FormRowGroup>

          {/* Row 5: Ghi chú */}
          <FormRow>
            <StyledLabel>Ghi chú</StyledLabel>
            <TextArea name="note" rows={2} placeholder="Ghi chú thêm..." value={formData.note} onChange={handleInputChange} />
          </FormRow>
        </ModalBody>
        <ModalFooter>
          <SaveButton onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 18, height: 18, borderWidth: 2, color: '#fff' }} />
                Đang lưu...
              </span>
            ) : (
              editingDiary ? 'Cập nhật' : 'Lưu nhật ký'
            )}
          </SaveButton>
          <CancelButton onClick={handleModalClose}>
            Hủy
          </CancelButton>
        </ModalFooter>
      </Modal>

      {/* Modal Vocab Test */}
      <Modal isOpen={showVocabTestModal} toggle={() => setShowVocabTestModal(false)} size="sm">
        <ModalHeader toggle={() => setShowVocabTestModal(false)}>
          Tải Test Từ Vựng
        </ModalHeader>
        <ModalBody>
          {selectedDiaryForTest && (
            <div style={{ marginBottom: '15px' }}>
              <p><strong>Lớp:</strong> {getClassName(selectedDiaryForTest.classId)}</p>
              <p><strong>Ngày:</strong> {formatDate(selectedDiaryForTest.lessonDate)}</p>
              <p><strong>Số từ vựng:</strong> {selectedDiaryForTest.vocabulary
                ? selectedDiaryForTest.vocabulary.split('\n').filter((line: string) => line.trim() !== '').length
                : 0} từ</p>
            </div>
          )}

          <FormGroup>
            <Label for="numberOfStudents">Số bài test (Số học sinh):</Label>
            <ReactstrapInput
              type="number"
              id="numberOfStudents"
              value={numberOfStudents}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (value > 50) {
                  setNumberOfStudents(50);
                } else if (value < 1) {
                  setNumberOfStudents(1);
                } else {
                  setNumberOfStudents(value);
                }
              }}
              onBlur={() => {
                if (numberOfStudents < 1) {
                  setNumberOfStudents(1);
                }
              }}
              min={1}
              max={50}
              step={1}
              style={{ width: '150px' }}
            />
            <small className="form-text text-muted">
              Sĩ số lớp: {students.filter((s: any) =>
                s.classId === selectedDiaryForTest?.classId && s.startDate !== null
              ).length} học sinh
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <SuccessButton onClick={handleDownloadVocabTest}>
            Tải xuống
          </SuccessButton>{' '}
          <SecondaryButton onClick={() => setShowVocabTestModal(false)}>
            Hủy
          </SecondaryButton>
        </ModalFooter>
      </Modal>

      {/* Modal Flashcard */}
      <Modal isOpen={showFlashcardModal} toggle={() => setShowFlashcardModal(false)} size="lg">
        <ModalHeader toggle={() => setShowFlashcardModal(false)}>
          Tải Flashcard
        </ModalHeader>
        <ModalBody>
          {selectedDiaryForFlashcard && (
            <div style={{ marginBottom: '15px' }}>
              <p><strong>Lớp:</strong> {getClassName(selectedDiaryForFlashcard.classId)}</p>
              <p><strong>Ngày:</strong> {formatDate(selectedDiaryForFlashcard.lessonDate)}</p>
              <p><strong>Số từ vựng ban đầu:</strong> {selectedDiaryForFlashcard.vocabulary
                ? selectedDiaryForFlashcard.vocabulary.split('\n').filter((line: string) => line.trim() !== '').length
                : 0} từ</p>
            </div>
          )}

          <FormGroup>
            <Label for="numberOfStudentsFlashcard">
              Số học sinh cần tạo Flashcard:
              {selectedDiaryForFlashcard && (() => {
                const attendanceSession = attendanceSessions.find((session: any) =>
                  session.classId === selectedDiaryForFlashcard.classId &&
                  session.date === selectedDiaryForFlashcard.lessonDate
                );
                if (!attendanceSession) {
                  return <span style={{ color: 'red', marginLeft: '8px', fontSize: '13px' }}>
                    (Chưa có điểm danh ngày {formatDate(selectedDiaryForFlashcard.lessonDate)})
                  </span>;
                }
                return null;
              })()}
            </Label>
            <ReactstrapInput
              type="number"
              id="numberOfStudentsFlashcard"
              value={numberOfStudentsFlashcard}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (value > 50) {
                  setNumberOfStudentsFlashcard(50);
                } else if (value < 0) {
                  setNumberOfStudentsFlashcard(0);
                } else {
                  setNumberOfStudentsFlashcard(value);
                }
                // Tính lại vocab khi thay đổi số học sinh
                if (selectedDiaryForFlashcard && value > 0) {
                  const { adjustedVocab } = calculateFlashcardVocab(
                    selectedDiaryForFlashcard.vocabulary || '',
                    value
                  );
                  setFlashcardVocabText(adjustedVocab);
                }
              }}
              min={0}
              max={50}
              step={1}
              style={{ width: '150px' }}
              required
            />
            <small className="form-text text-muted">
              {selectedDiaryForFlashcard && (() => {
                const attendanceSession = attendanceSessions.find((session: any) =>
                  session.classId === selectedDiaryForFlashcard.classId &&
                  session.date === selectedDiaryForFlashcard.lessonDate
                );

                const totalStudents = students.filter((s: any) =>
                  s.classId === selectedDiaryForFlashcard.classId && s.startDate !== null
                ).length;

                if (attendanceSession) {
                  const presentCount = attendanceStudentRecords.filter((record: any) =>
                    record.sessionId === attendanceSession.id && record.isPresent === true
                  ).length;
                  return `Đi học: ${presentCount}/${totalStudents} học sinh`;
                }

                return `Sĩ số lớp: ${totalStudents} học sinh`;
              })()}
            </small>
          </FormGroup>

          <FormGroup>
            <Label for="flashcardVocab">Danh sách từ vựng (đã điều chỉnh):</Label>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
              Mỗi từ và nghĩa nằm trên 1 hàng, ngăn cách nhau bởi dấu hai chấm (:)
            </p>
            <ReactstrapInput
              type="textarea"
              id="flashcardVocab"
              value={flashcardVocabText}
              onChange={(e) => setFlashcardVocabText(e.target.value)}
              rows={10}
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
            <small className="form-text text-muted">
              Tổng số từ: {flashcardVocabText.split('\n').filter(line => line.trim() !== '').length} từ
              {selectedDiaryForFlashcard && (() => {
                const { sheetsNeeded } = calculateFlashcardVocab(
                  selectedDiaryForFlashcard.vocabulary || '',
                  numberOfStudentsFlashcard
                );
                return ` | Cần ${sheetsNeeded} tờ A4`;
              })()}
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <SuccessButton onClick={handleDownloadFlashcard}>
            Tải xuống
          </SuccessButton>{' '}
          <SecondaryButton onClick={() => setShowFlashcardModal(false)}>
            Hủy
          </SecondaryButton>
        </ModalFooter>
      </Modal>

      {/* Modal xem từ vựng */}
      <Modal isOpen={showVocabViewModal} toggle={() => setShowVocabViewModal(false)} size="lg">
        <ModalHeader toggle={() => setShowVocabViewModal(false)}>
          {viewModalTitle}
        </ModalHeader>
        <ModalBody>
          <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
            <strong>Tổng số từ vựng:</strong> {viewModalContent.split('\n').filter(line => line.trim() !== '').length} từ
          </div>
          <div style={{
            whiteSpace: 'pre-wrap',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontSize: '14px',
            maxHeight: '60vh',
            overflowY: 'auto',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #dee2e6',
            lineHeight: '1.6'
          }}>
            {viewModalContent || 'Không có nội dung'}
          </div>
        </ModalBody>
        <ModalFooter style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            {viewModalDiary && (
              /* Flashcard Buttons inside Vocab Modal */
              viewModalDiary.flashcardSetId ? (
                <SuccessButton
                  onClick={() => {
                    handleReviewFlashcard(viewModalDiary.flashcardSetId);
                    setShowVocabViewModal(false);
                  }}
                  title="Ôn tập flashcard"
                >
                  Ôn tập bộ từ
                </SuccessButton>
              ) : (
                <PrimaryButton
                  onClick={() => handleCreateFlashcardSet(viewModalDiary.id)}
                  title="Tạo bộ từ flashcard"
                  disabled={creatingFlashcardSetId === viewModalDiary.id}
                >
                  {creatingFlashcardSetId === viewModalDiary.id ? '⏳ Đang tạo...' : 'Tạo bộ từ Flashcard'}
                </PrimaryButton>
              )
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <PrimaryButton
              onClick={() => handleCopyVocabulary(viewModalContent)}
            >
              Copy
            </PrimaryButton>
            <SecondaryButton onClick={() => setShowVocabViewModal(false)}>
              Đóng
            </SecondaryButton>
          </div>
        </ModalFooter>
      </Modal>

      {/* Modal xem mẫu câu */}
      <Modal isOpen={showSampleSentencesViewModal} toggle={() => setShowSampleSentencesViewModal(false)} size="lg">
        <ModalHeader toggle={() => setShowSampleSentencesViewModal(false)}>
          {viewModalTitle}
        </ModalHeader>
        <ModalBody>
          <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
            <strong>Tổng số mẫu câu:</strong> {viewModalContent.split('\n').filter(line => line.trim() !== '').length} câu
          </div>
          <div style={{
            whiteSpace: 'pre-wrap',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontSize: '14px',
            maxHeight: '60vh',
            overflowY: 'auto',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #dee2e6',
            lineHeight: '1.6'
          }}>
            {viewModalContent || 'Không có nội dung'}
          </div>
        </ModalBody>
        <ModalFooter>
          <PrimaryButton
            onClick={() => {
              handleCopySampleSentences(viewModalContent);
            }}
          >
            Copy
          </PrimaryButton>{' '}
          <SecondaryButton onClick={() => setShowSampleSentencesViewModal(false)}>
            Đóng
          </SecondaryButton>
        </ModalFooter>
      </Modal>

      {/* Modal sửa ghi chú */}
      <Modal isOpen={showNoteModal} toggle={closeNoteModal} size="lg">
        <ModalHeader toggle={closeNoteModal}>
          Sửa ghi chú
        </ModalHeader>
        <ModalBody>
          <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
            {noteModalDiary ? `${getClassName(noteModalDiary.classId)} — ${formatDate(noteModalDiary.lessonDate)}` : ''}
          </div>
          <div style={{
            whiteSpace: 'pre-wrap',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontSize: '14px'
          }}>
            <ReactstrapInput
              type="textarea"
              value={noteModalValue}
              onChange={(e: any) => setNoteModalValue(e.target.value)}
              rows={8}
              style={{ fontSize: '14px' }}
              placeholder="Nhập ghi chú..."
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <PrimaryButton onClick={saveNoteQuickEdit} disabled={isLoading}>
            Lưu
          </PrimaryButton>{' '}
          <SecondaryButton onClick={closeNoteModal} disabled={isLoading}>
            Đóng
          </SecondaryButton>
        </ModalFooter>
      </Modal>

      {/* Modal Writing */}
      <Modal isOpen={showWritingModal} toggle={() => setShowWritingModal(false)} size="md">
        <ModalHeader toggle={() => setShowWritingModal(false)}>
          Tải file Writing
        </ModalHeader>
        <ModalBody>
          {selectedDiaryForWriting && (
            <div style={{ marginBottom: '15px' }}>
              <p><strong>Lớp:</strong> {getClassName(selectedDiaryForWriting.classId)}</p>
              <p><strong>Ngày:</strong> {formatDate(selectedDiaryForWriting.lessonDate)}</p>
              <p><strong>Số từ vựng:</strong> {selectedDiaryForWriting.vocabulary
                ? selectedDiaryForWriting.vocabulary.split('\n').filter((line: string) => line.trim() !== '').length
                : 0} từ</p>
              <p><strong>Số mẫu câu:</strong> {selectedDiaryForWriting.sampleSentences
                ? selectedDiaryForWriting.sampleSentences.split('\n').filter((line: string) => line.trim() !== '').length
                : 0} câu</p>
            </div>
          )}

          <FormGroup>
            <Label for="numberOfStudentsWriting">
              Số học sinh cần in:
              {selectedDiaryForWriting && (() => {
                const attendanceSession = attendanceSessions.find((session: any) =>
                  session.classId === selectedDiaryForWriting.classId &&
                  session.date === selectedDiaryForWriting.lessonDate
                );
                if (!attendanceSession) {
                  return <span style={{ color: 'red', marginLeft: '8px', fontSize: '13px' }}>
                    (Chưa có điểm danh ngày {formatDate(selectedDiaryForWriting.lessonDate)})
                  </span>;
                }
                return null;
              })()}
            </Label>
            <ReactstrapInput
              type="number"
              id="numberOfStudentsWriting"
              value={numberOfStudentsWriting}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (value > 50) {
                  setNumberOfStudentsWriting(50);
                } else if (value < 0) {
                  setNumberOfStudentsWriting(0);
                } else {
                  setNumberOfStudentsWriting(value);
                }
              }}
              min={0}
              max={50}
              step={1}
              style={{ width: '150px' }}
              required
            />
            <small className="form-text text-muted">
              {selectedDiaryForWriting && (() => {
                const attendanceSession = attendanceSessions.find((session: any) =>
                  session.classId === selectedDiaryForWriting.classId &&
                  session.date === selectedDiaryForWriting.lessonDate
                );

                const totalStudents = students.filter((s: any) =>
                  s.classId === selectedDiaryForWriting.classId && s.startDate !== null
                ).length;

                if (attendanceSession) {
                  const presentCount = attendanceStudentRecords.filter((record: any) =>
                    record.sessionId === attendanceSession.id && record.isPresent === true
                  ).length;
                  return `Đi học: ${presentCount}/${totalStudents} học sinh`;
                }

                return `Sĩ số lớp: ${totalStudents} học sinh`;
              })()}
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <SuccessButton onClick={handleDownloadWriting} disabled={numberOfStudentsWriting === 0}>
            Tải xuống
          </SuccessButton>{' '}
          <SecondaryButton onClick={() => setShowWritingModal(false)}>
            Hủy
          </SecondaryButton>
        </ModalFooter>
      </Modal>

      {/* ── Modal cảnh báo định dạng mẫu câu không hợp lệ ── */}
      <Modal isOpen={showSentenceFormatModal} toggle={() => setShowSentenceFormatModal(false)} centered>
        <ModalHeader toggle={() => setShowSentenceFormatModal(false)}>
          ⚠️ Mẫu câu không đúng định dạng
        </ModalHeader>
        <ModalBody>
          <p style={{ marginBottom: '12px', color: '#374151', lineHeight: 1.6 }}>
            Phần <strong>Mẫu câu</strong> có nội dung nhưng không có dòng nào thỏa mãn định dạng hợp lệ.
            Vui lòng chỉnh sửa theo một trong các định dạng sau:
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Định dạng 1 */}
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '12px 16px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#0369a1', marginBottom: '6px' }}>📌 Định dạng 1 — 1 dòng, ngăn cách bằng dấu<code style={{ marginLeft: 4, background: '#e0f2fe', padding: '1px 5px', borderRadius: 4 }}>:</code></div>
              <pre style={{ margin: 0, fontSize: '13px', color: '#0c4a6e', background: 'transparent' }}>{`She studies hard: Cô ấy học chăm chỉ
He goes to school: Anh ấy đi học`}</pre>
            </div>

            {/* Định dạng 2 */}
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 16px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#15803d', marginBottom: '6px' }}>📌 Định dạng 2 — 1 dòng, ngăn cách bằng dấu<code style={{ marginLeft: 4, background: '#dcfce7', padding: '1px 5px', borderRadius: 4 }}>→</code></div>
              <pre style={{ margin: 0, fontSize: '13px', color: '#14532d', background: 'transparent' }}>{`She studies hard → Cô ấy học chăm chỉ
He goes to school → Anh ấy đi học`}</pre>
            </div>

            {/* Định dạng 3 */}
            <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px 16px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#a16207', marginBottom: '6px' }}>📌 Định dạng 3 — 2 dòng (tiếng Anh + tiếng Việt liền kề)</div>
              <pre style={{ margin: 0, fontSize: '13px', color: '#713f12', background: 'transparent' }}>{`1. She studies very hard every day
→ Cô ấy học rất chăm chỉ mỗi ngày

2. He goes to school by bike
Anh ấy đi học bằng xe đạp`}</pre>
              <div style={{ fontSize: '11px', color: '#92400e', marginTop: '8px' }}>
                💡 Số thứ tự đầu dòng (1. 2. ...) và dấu → đầu dòng tiếng Việt sẽ được bỏ qua tự động.
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <PrimaryButton onClick={() => setShowSentenceFormatModal(false)}>
            Đã hiểu, đi sửa mẫu câu
          </PrimaryButton>
        </ModalFooter>
      </Modal>

      {/* ── Modal Tải nhật kí ─────────────────────────────────────────────── */}
      {showDownloadDiaryModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1100,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowDownloadDiaryModal(false); }}
        >
          <div style={{
            background: '#fff', borderRadius: 20,
            boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
            width: '100%', maxWidth: 500,
            overflow: 'hidden', animation: 'fadeInUp .22s ease',
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <span style={{ fontSize: 22 }}>📥</span>
              <div>
                <div style={{ color: '#fff', fontWeight: 800, fontSize: 17 }}>Tải nhật kí dạy học</div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, marginTop: 2 }}>
                  Xuất nhật kí theo lớp và khoảng thời gian
                </div>
              </div>
              <button
                onClick={() => setShowDownloadDiaryModal(false)}
                style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
              >×</button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Chọn lớp */}
              <div>
                <label style={{ display: 'block', fontWeight: 700, fontSize: 13, color: '#475569', marginBottom: 6 }}>
                  🏫 Lớp học <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={dlClassId ?? ''}
                  onChange={e => setDlClassId(e.target.value ? Number(e.target.value) : null)}
                  style={{
                    width: '100%', padding: '9px 12px', borderRadius: 10,
                    border: '1.5px solid #e2e8f0', fontSize: 14,
                    outline: 'none', background: '#f8fafc', color: '#1e293b',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">-- Chọn lớp --</option>
                  {classes.slice().sort((a: any, b: any) => a.name.localeCompare(b.name)).map((cls: any) => (
                    <option key={cls.id} value={cls.id}>{cls.name}</option>
                  ))}
                </select>
              </div>

              {/* Khoảng thời gian */}
              <div>
                <label style={{ display: 'block', fontWeight: 700, fontSize: 13, color: '#475569', marginBottom: 6 }}>
                  📅 Khoảng thời gian <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4, fontWeight: 600 }}>Từ ngày</div>
                    <input
                      type="date"
                      value={dlDateFrom}
                      onChange={e => setDlDateFrom(e.target.value)}
                      style={{
                        width: '100%', padding: '8px 10px', borderRadius: 10,
                        border: '1.5px solid #e2e8f0', fontSize: 14, background: '#f8fafc',
                        outline: 'none', color: '#1e293b',
                      }}
                    />
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: 16, paddingTop: 20 }}>→</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4, fontWeight: 600 }}>Đến ngày</div>
                    <input
                      type="date"
                      value={dlDateTo}
                      onChange={e => setDlDateTo(e.target.value)}
                      style={{
                        width: '100%', padding: '8px 10px', borderRadius: 10,
                        border: '1.5px solid #e2e8f0', fontSize: 14, background: '#f8fafc',
                        outline: 'none', color: '#1e293b',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Mục nội dung */}
              <div>
                <label style={{ display: 'block', fontWeight: 700, fontSize: 13, color: '#475569', marginBottom: 10 }}>
                  📋 Nội dung muốn xuất <span style={{ color: '#ef4444' }}>*</span>
                  <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6 }}>(chọn ít nhất 1 mục)</span>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {([
                    { key: 'vocabulary', label: 'Từ vựng', icon: '📚', color: '#6366f1' },
                    { key: 'sampleSentences', label: 'Mẫu câu', icon: '💬', color: '#0891b2' },
                    { key: 'contentTaught', label: 'Nội dung đã dạy', icon: '✏️', color: '#059669' },
                    { key: 'homework', label: 'BTVN', icon: '📝', color: '#dc2626' },
                  ] as { key: keyof typeof dlSections; label: string; icon: string; color: string }[]).map(({ key, label, icon, color }) => {
                    const checked = dlSections[key];
                    return (
                      <div
                        key={key}
                        onClick={() => setDlSections(s => ({ ...s, [key]: !s[key] }))}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 14px', borderRadius: 12, cursor: 'pointer',
                          border: `1.5px solid ${checked ? color : '#e2e8f0'}`,
                          background: checked ? `${color}12` : '#f8fafc',
                          transition: 'all 0.15s',
                        }}
                      >
                        <div style={{
                          width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                          border: `2px solid ${checked ? color : '#cbd5e1'}`,
                          background: checked ? color : '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {checked && <span style={{ color: '#fff', fontSize: 11, fontWeight: 900 }}>✓</span>}
                        </div>
                        <span style={{ fontSize: 13 }}>{icon}</span>
                        <span style={{ fontSize: 13, fontWeight: checked ? 700 : 400, color: checked ? color : '#64748b' }}>
                          {label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer: format selector + action buttons */}
            <div style={{
              padding: '16px 24px', borderTop: '1px solid #f1f5f9',
            }}>
              {/* Format selector */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>📄 Định dạng file</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {(['doc', 'pdf'] as const).map(fmt => (
                    <div
                      key={fmt}
                      onClick={() => setDlFormat(fmt)}
                      style={{
                        flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px', borderRadius: 12, cursor: 'pointer',
                        border: `1.5px solid ${dlFormat === fmt ? '#6366f1' : '#e2e8f0'}`,
                        background: dlFormat === fmt ? 'rgba(99,102,241,0.08)' : '#f8fafc',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{
                        width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                        border: `2px solid ${dlFormat === fmt ? '#6366f1' : '#cbd5e1'}`,
                        background: dlFormat === fmt ? '#6366f1' : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {dlFormat === fmt && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', display: 'block' }} />}
                      </div>
                      <span style={{ fontSize: 14 }}>{fmt === 'doc' ? '📝' : '📕'}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: dlFormat === fmt ? 700 : 400, color: dlFormat === fmt ? '#4f46e5' : '#374151' }}>
                          {fmt === 'doc' ? 'Word (.doc)' : 'PDF'}
                        </div>
                        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>
                          {fmt === 'doc' ? 'Mở bằng Microsoft Word' : 'Tải file PDF về máy'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  onClick={() => setShowDownloadDiaryModal(false)}
                  style={{
                    padding: '9px 20px', borderRadius: 10, border: '1.5px solid #e2e8f0',
                    background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >Huỷ</button>
                <button
                  onClick={handleDownloadDiary}
                  disabled={dlLoading || !dlClassId || !Object.values(dlSections).some(Boolean)}
                  style={{
                    padding: '9px 22px', borderRadius: 10, border: 'none',
                    background: dlLoading || !dlClassId ? '#c4b5fd' : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                    color: '#fff', fontSize: 13, fontWeight: 700, cursor: dlLoading ? 'wait' : 'pointer',
                    boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
                    display: 'flex', alignItems: 'center', gap: 7,
                  }}
                >
                  {dlLoading ? (
                    <><span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.5)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /> Đang tải...</>
                  ) : (
                    <>{dlFormat === 'doc' ? '📝' : '📕'} Tải {dlFormat === 'doc' ? 'Word' : 'PDF'}</>
                  )}
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </PageWrapper>
  );
};

export default LessonDiary;
